import { useCallback } from 'react'
import { useQuery } from '@apollo/client/react'

import { graphql } from '~publish/graphql'
import type { ResultOf } from '~publish/graphql'

export const ORGANIZATION_POSTING_GOALS_SUMMARY_QUERY = graphql(/* GraphQL */ `
  query OrganizationPostingGoalsSummary(
    $input: OrganizationPostingGoalsSummaryInput!
  ) {
    organizationPostingGoalsSummary(input: $input) {
      channelGoals {
        channel {
          id
          name
          displayName
          avatar
          service
        }
        postingGoal {
          goal
          sentCount
          scheduledCount
          status
        }
      }
    }
  }
`)

/** A single channel's posting goal progress, as returned by the summary query. */
export type PostingGoalChannel = ResultOf<
  typeof ORGANIZATION_POSTING_GOALS_SUMMARY_QUERY
>['organizationPostingGoalsSummary']['channelGoals'][number]

type UsePostingGoalsSummaryResult = {
  /** Per-channel goal progress, used to break down which goals are unmet. */
  goals: PostingGoalChannel[]
  completed: number
  total: number
  loading: boolean
  hasData: boolean
  refresh: () => void
}

export const usePostingGoalsSummary = (
  organizationId: string | undefined,
): UsePostingGoalsSummaryResult => {
  const { data, loading, refetch } = useQuery(
    ORGANIZATION_POSTING_GOALS_SUMMARY_QUERY,
    {
      variables: {
        input: {
          organizationId: organizationId ?? '',
        },
      },
      skip: !organizationId,
    },
  )

  const refresh = useCallback((): void => {
    void refetch().catch(() => undefined)
  }, [refetch])

  const goals = data?.organizationPostingGoalsSummary.channelGoals ?? []
  const completed = goals.filter(
    (channelGoal) => channelGoal.postingGoal.status === 'Hit',
  ).length

  return {
    goals,
    completed,
    total: goals.length,
    loading,
    hasData: data != null,
    refresh,
  }
}
